import React, { useState } from "react";
import { Package, Truck, Loader, Filter } from "lucide-react";
import {
  AssignedCart,
  DeliveryOrder,
  Group,
} from "../../../../redux/deliveryRouteOptimizer";
import "./index.css";
import ActualGroupDetailsView from "../ActualGroupDetailView";
import VehicleRoutes from "../VehicleRoute";
import GroupDetailsView from "../GroupDetailsView";
import useGroupOrderPrint from "../../../../hooks/useGroupOrderPrint";
import CartAssignmentSection from "../CartAssignmentSection";
import AssignedCartsSection from "../AssignedCartsSection";

interface DeliveryPanelsProps {
  // Basic data props
  deliveries: DeliveryOrder[];
  groups: Record<string, Group>;
  vehicles: AssignedCart[];

  // Workflow and view state
  workflowStep: "group" | "assign" | "actual";
  setWorkflowStep: (step: "group" | "assign" | "actual") => void;

  // Selection states
  selectedDeliveries: string[];
  toggleDeliverySelection: (id: string) => void;

  // Map interaction
  setHoveredDelivery: (id: string | null) => void;

  // Group and vehicle assignment
  assignVehicleToGroup: (groupId: string, vehicleId: string) => void;
  isAssigningCart?: boolean;
  assigningGroupId?: string | null;

  // Group management
  addToGroup: (groupId: string) => void;
  setShowGroupModal: (show: boolean) => void;
  canCreateGroup: boolean;
  deleteGroup: (groupId: string) => void;
  onSaveGroupChanges: (groupId: string, orderIds: string[]) => void;

  // Search and filtering
  filteredDeliveries: DeliveryOrder[];

  // Order selection for map
  selectedOrdersForMap: string[];
  showOnlySelectedOrders: boolean;
  toggleOrderSelectionForMap: (id: string) => void;
  toggleSelectAllOrdersForMap: () => void;
  setShowOnlySelectedOrders: (show: boolean) => void;
  setActiveMapFilter: (filter: "none" | "group" | "orders") => void;

  // Cart selection for map
  selectedCartsForMap: string[];
  toggleCartSelectionForMap: (cartId: string) => void;
  toggleSelectAllCartsForMap: () => void;
  showOnlySelectedCarts: boolean;
  setShowOnlySelectedCarts: (show: boolean) => void;

  // Group selection for map
  selectedGroupsForMap: string[];
  toggleGroupSelectionForMap: (groupId: string) => void;
  showOnlySelectedGroups: boolean;
  setShowOnlySelectedGroups: (show: boolean) => void;

  // Filter states
  groupTypeFilter: "tentative" | "finalized";
  tentativeFilter: "all" | "assigned" | "unassigned";
  onGroupTypeFilterChange: (filter: "tentative" | "finalized") => void;
  onTentativeFilterChange: (filter: "all" | "assigned" | "unassigned") => void;

  // Actual group creation
  onCreateActualGroupOrders?: (selectedGroupIds?: string[]) => void;
  isCreatingActualGroups?: boolean;

  removeCartFromGroup: (groupId: string) => void;
  onOptimizePath?: (
    groupId: string,
    orderIds: string[],
    isManuallyOptimized: boolean,
  ) => void;
  onUpdateGroupOrderStatus?: (groupId: string, newStatus: string) => void;
  isUpdatingGroupStatus?: boolean;
  updatingGroupId?: string | null;

  onRemoveOrderFromGroup?: (groupId: string, orderId: string) => void;
  isRemovingOrder?: boolean;
  removingOrderId?: string | null;

  // New props for group selection
  selectedGroupsForActual?: string[];
  toggleGroupSelectionForActual?: (groupId: string) => void;
  toggleSelectAllGroupsForActual?: () => void;

  // Refresh props
  onRefreshLeftPanel?: () => void;
  isRefreshingLeftPanel?: boolean;
}

// Helper function to get optimized route order
const getOptimizedRouteOrder = (group: Group) => {
  if (group.deliveryRoute?.route && group.deliveryRoute.route.length > 0) {
    return group.deliveryRoute.route.map((routePoint) => routePoint.orderId);
  }
  return group.deliveryIds || [];
};

export const DeliveryPanels: React.FC<DeliveryPanelsProps> = ({
  deliveries,
  groups,
  vehicles,
  workflowStep,
  setWorkflowStep,
  selectedDeliveries,
  toggleDeliverySelection,
  setHoveredDelivery,
  assignVehicleToGroup,
  isAssigningCart = false,
  assigningGroupId = null,
  addToGroup,
  setShowGroupModal,
  canCreateGroup,
  deleteGroup,
  onSaveGroupChanges,
  filteredDeliveries,
  selectedOrdersForMap = [],
  showOnlySelectedOrders = false,
  toggleOrderSelectionForMap = () => {},
  toggleSelectAllOrdersForMap = () => {},
  setShowOnlySelectedOrders = () => {},
  setActiveMapFilter = () => {},
  selectedCartsForMap = [],
  toggleCartSelectionForMap = () => {},
  toggleSelectAllCartsForMap = () => {},
  showOnlySelectedCarts = false,
  setShowOnlySelectedCarts = () => {},
  selectedGroupsForMap = [],
  toggleGroupSelectionForMap = () => {},
  showOnlySelectedGroups = false,
  setShowOnlySelectedGroups = () => {},
  groupTypeFilter = "tentative",
  tentativeFilter = "all",
  onGroupTypeFilterChange = () => {},
  onTentativeFilterChange = () => {},
  onCreateActualGroupOrders = () => {},
  isCreatingActualGroups = false,
  removeCartFromGroup,
  onOptimizePath,
  onUpdateGroupOrderStatus = () => {},
  onRemoveOrderFromGroup = () => {},
  isRemovingOrder = false,
  removingOrderId = null,
  isUpdatingGroupStatus = false,
  updatingGroupId = null,
  selectedGroupsForActual = [],
  toggleGroupSelectionForActual = () => {},
  toggleSelectAllGroupsForActual = () => {},
  onRefreshLeftPanel = () => {},
  isRefreshingLeftPanel = false,
}) => {
  // Filter states for orders tab
  const [dispatchFilter, setDispatchFilter] = useState<
    "all" | "dispatched" | "undispatched"
  >("all");
  const [assignmentFilter, setAssignmentFilter] = useState<
    "all" | "unassigned" | "assigned"
  >("all");

  // Vehicle filter state for actual tab
  const [vehicleFilter, setVehicleFilter] = useState<"assigned" | "unassigned">(
    "assigned",
  );

  // Collapse state for assigned carts
  const [expandedCarts, setExpandedCarts] = useState<Set<string>>(new Set());

  // Use the printing hook
  const { printGroupOrdersByCart, isPrinting } = useGroupOrderPrint({
    groups: Object.values(groups),
    deliveries: deliveries,
    vehicles: vehicles,
  });
  const [localSearchTerm, setLocalSearchTerm] = useState("");

  // Filter out collection groups - only show groups where isCollectionGrouped is false
  const regularGroups = Object.values(groups).filter(
    (group) => !group.isCollectionGrouped,
  );

  // Get collection groups for actual step
  const collectionGroups = Object.values(groups).filter(
    (group) => group.isCollectionGrouped,
  );

  // Get assignable groups (with carts assigned)
  const getAssignableGroups = () => {
    return regularGroups.filter((group) => group.cart?.cartId);
  };

  // Filter only movable carts (isFixedLocation: false)
  const movableCarts = vehicles.filter((vehicle) => !vehicle.isFixedLocation);

  // Get carts that are NOT assigned to ANY groups
  const getUnassignedCarts = () => {
    const assignedCartIds = new Set();

    // Collect all assigned deliveryCartIds
    Object.values(groups).forEach((group) => {
      if (group.cart) {
        const deliveryCartId = (group.cart as any)?.deliveryCartId;
        if (deliveryCartId) {
          assignedCartIds.add(deliveryCartId);
        } else if (group.cart.cartId) {
          assignedCartIds.add(group.cart.cartId);
        }
      }
    });

    return movableCarts.filter((vehicle) => !assignedCartIds.has(vehicle.id));
  };

  // Get carts that ARE assigned to ANY groups (both tentative AND collection groups)
  const getAssignedCarts = () => {
    const assignedCartIds = new Set();

    // Iterate through all groups to collect assigned deliveryCartId
    Object.values(groups).forEach((group) => {
      if (group.cart) {
        // Check both cartId and deliveryCartId
        const deliveryCartId = (group.cart as any)?.deliveryCartId;
        if (deliveryCartId) {
          assignedCartIds.add(deliveryCartId);
        } else if (group.cart.cartId) {
          // Fallback to cartId if deliveryCartId doesn't exist
          assignedCartIds.add(group.cart.cartId);
        }
      }
    });

    return movableCarts.filter((vehicle) => assignedCartIds.has(vehicle.id));
  };

  // Handle cart printing
  const handleCartPrint = async (
    cartId: string,
    cartName: string,
    copyType: "rider" | "customer",
  ) => {
    try {
      await printGroupOrdersByCart({
        cartId,
        cartName,
        copyType,
      });
    } catch (error) {
      console.error("Error printing cart orders:", error);
    }
  };

  // Collapse helpers
  const toggleCartExpansion = (cartId: string) => {
    setExpandedCarts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cartId)) {
        newSet.delete(cartId);
      } else {
        newSet.add(cartId);
      }
      return newSet;
    });
  };

 const getFilteredDeliveriesByDispatch = () => {
  const isFinalizedGroup = (delivery: DeliveryOrder) => {
    return (
      delivery.groupedForCollection === true
    );
  };

  switch (dispatchFilter) {
    case "dispatched":
      // Show ONLY finalized groups
      return filteredDeliveries.filter(isFinalizedGroup);
    case "undispatched":
      // Show all orders EXCEPT finalized groups
      return filteredDeliveries.filter(d => !isFinalizedGroup(d));
    case "all":
    default:
      // Show all orders EXCEPT finalized groups
      return filteredDeliveries.filter(d => !isFinalizedGroup(d));
  }
};

  const getFilteredDeliveriesByAssignment = () => {
    const dispatchFiltered = getFilteredDeliveriesByDispatch();

    const allDeliveries = dispatchFiltered;

    // Apply local search filter if search term exists
    let searchFiltered = allDeliveries;
    if (localSearchTerm.trim()) {
      const searchLower = localSearchTerm.toLowerCase();
      searchFiltered = allDeliveries.filter((delivery) =>
        delivery.orderReference?.toLowerCase().includes(searchLower),
      );
    }

    switch (assignmentFilter) {
      case "unassigned":
        return searchFiltered.filter(
          (delivery) => delivery.status === "unassigned" && !delivery.groupId,
        );
      case "assigned":
        return searchFiltered.filter(
          (delivery) => delivery.status === "grouped" || delivery.groupId,
        );
      default:
        return searchFiltered;
    }
  };

  const finalFilteredDeliveries = getFilteredDeliveriesByAssignment();

  // Get groups based on tentative filters
  const getFilteredGroups = () => {
    switch (tentativeFilter) {
      case "assigned":
        return regularGroups.filter((group) => group.cart?.cartId);
      case "unassigned":
        return regularGroups.filter((group) => !group.cart?.cartId);
      default:
        return regularGroups;
    }
  };

  const filteredGroups = getFilteredGroups();

  // Get order status display text and color
  const getOrderStatusInfo = (orderStatus: string) => {
    switch (orderStatus) {
      case "IssuedByKitchen":
        return { text: "Issued By Kitchen", color: "#10b981" };
      case "ArrivedAtCart":
        return { text: "Issued By Kitchen", color: "#10b981" };
      case "Completed":
        return { text: "Completed", color: "#6b7280" };
      case "ApprovedByRestaurant":
        return { text: "Approved By Restaurant", color: "#f59e0b" };
      case "Pending":
        return { text: "Pending", color: "#ef4444" };
      default:
        return { text: orderStatus, color: "#6b7280" };
    }
  };

  // Get vehicle display name with additional info
  const getVehicleDisplayName = (vehicle: AssignedCart) => {
    return vehicle.name;
  };

  // Check if a group is currently being assigned a cart
  const isGroupAssigning = (groupId: string) => {
    return isAssigningCart && assigningGroupId === groupId;
  };

  // Handle filter changes
  const handleTentativeFilterChange = (
    filter: "all" | "assigned" | "unassigned",
  ) => {
    if (onTentativeFilterChange) {
      onTentativeFilterChange(filter);
    }
  };

  const handleGroupTypeFilterChange = (filter: "tentative" | "finalized") => {
    if (onGroupTypeFilterChange) {
      onGroupTypeFilterChange(filter);
    }
    if (filter === "tentative") {
      handleTentativeFilterChange("all");
    }
  };

  const handleVehicleFilterChange = (filter: "assigned" | "unassigned") => {
    setVehicleFilter(filter);
  };

  // Handle individual order selection for MAP VIEW ONLY
  const handleOrderSelectionForMap = (orderId: string) => {
    // Toggle for map selection only
    toggleOrderSelectionForMap(orderId);

    // Auto-enable map filter when selecting orders
    if (!showOnlySelectedOrders && selectedOrdersForMap.length === 0) {
      setShowOnlySelectedOrders(true);
      setActiveMapFilter("orders");
    }
  };

  // Handle individual order selection for GROUP CREATION (unassigned filter only)
  const handleOrderSelectionForGroup = (orderId: string) => {
    // Toggle for group creation selection
    toggleDeliverySelection(orderId);

    // Also toggle for map selection to show on map
    toggleOrderSelectionForMap(orderId);

    // Auto-enable map filter when selecting orders
    if (!showOnlySelectedOrders && selectedOrdersForMap.length === 0) {
      setShowOnlySelectedOrders(true);
      setActiveMapFilter("orders");
    }
  };

  // Check if we should show group creation actions (when in unassigned filter AND orders are selected)
  const shouldShowGroupActions = selectedDeliveries.length > 0;

  // Render unassigned carts section (simple list without print buttons)
  const renderUnassignedCartsSection = () => {
    const unassignedCarts = getUnassignedCarts();

    if (unassignedCarts.length === 0) {
      return (
        <div className="delivery-panel__empty-state">
          <Truck size={32} />
          <p>No unassigned carts</p>
          <p>All movable carts are assigned to groups</p>
        </div>
      );
    }

    return unassignedCarts.map((cart) => (
      <div key={cart.id} className="cart-item">
        <div className="cart-item__header">
          <div className="cart-item__info">
            <div className="cart-item__color" />
            <div className="cart-item__details">
              <div className="cart-item__name">{cart.name}</div>
              <div className="cart-item__status">
                <Truck size={12} />
                <span>Available for assignment</span>
              </div>

              {cart.cartState && (
                <div className="cart-item__state">Status: {cart.cartState}</div>
              )}
            </div>
          </div>
        </div>
      </div>
    ));
  };

  return (
    <div className="delivery-panels">
      <div className="delivery-panels__wrapper">
        {isRefreshingLeftPanel && (
          <div className="delivery-panels__loading-overlay">
            <div className="delivery-panels__loading-spinner">
              <Loader size={32} className="animate-spin" />
              <span>Refreshing data...</span>
            </div>
          </div>
        )}

        <div
          className={`delivery-panels__content ${isRefreshingLeftPanel ? "delivery-panels__content--loading" : ""}`}
        >
          <div className="delivery-panels__workflow-steps">
            <button
              onClick={() => setWorkflowStep("group")}
              className={`delivery-panels__workflow-button ${
                workflowStep === "group"
                  ? "delivery-panels__workflow-button--active"
                  : ""
              }`}
            >
              Orders
            </button>
            <span className="delivery-panels__workflow-separator"></span>
            <button
              onClick={() => setWorkflowStep("assign")}
              className={`delivery-panels__workflow-button ${
                workflowStep === "assign"
                  ? "delivery-panels__workflow-button--active"
                  : ""
              }`}
            >
              Group
            </button>
            <span className="delivery-panels__workflow-separator"></span>
            <button
              onClick={() => setWorkflowStep("actual")}
              className={`delivery-panels__workflow-button ${
                workflowStep === "actual"
                  ? "delivery-panels__workflow-button--active"
                  : ""
              }`}
            >
              Vehicle
            </button>

            {/* Add Refresh Button */}
            <div className="delivery-panels__refresh-container">
              <button
                onClick={onRefreshLeftPanel}
                disabled={isRefreshingLeftPanel}
                className="delivery-panels__refresh-button"
                title="Refresh left panel data"
              >
                {isRefreshingLeftPanel ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Refreshing...
                  </>
                ) : (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M23 4v6h-6M1 20v-6h6" />
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                    Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {workflowStep === "group" && (
            <>
              {/* Search Bar */}
              <div className="delivery-panels__search-container">
                <div className="delivery-panels__search-wrapper">
                  <input
                    type="text"
                    value={localSearchTerm}
                    onChange={(e) => setLocalSearchTerm(e.target.value)}
                    placeholder="Search by order reference..."
                    className="delivery-panels__search-input"
                  />
                </div>
              </div>

              {/* Dispatch Filters */}
              <div className="delivery-panels__filters">
                <div className="delivery-panels__filter-group">
                  <div className="delivery-panels__filter-buttons">
                    <button
                      onClick={() => setDispatchFilter("all")}
                      className={`delivery-panels__filter-button ${
                        dispatchFilter === "all"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setDispatchFilter("dispatched")}
                      className={`delivery-panels__filter-button ${
                        dispatchFilter === "dispatched"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      Dispatched
                    </button>
                    <button
                      onClick={() => setDispatchFilter("undispatched")}
                      className={`delivery-panels__filter-button ${
                        dispatchFilter === "undispatched"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      Undispatched
                    </button>
                  </div>
                </div>

                <div className="delivery-panels__filter-group">
                  <div className="delivery-panels__filter-buttons">
                    <button
                      onClick={() => setAssignmentFilter("all")}
                      className={`delivery-panels__filter-button ${
                        assignmentFilter === "all"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setAssignmentFilter("unassigned")}
                      className={`delivery-panels__filter-button ${
                        assignmentFilter === "unassigned"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      Unassigned
                    </button>
                    <button
                      onClick={() => setAssignmentFilter("assigned")}
                      className={`delivery-panels__filter-button ${
                        assignmentFilter === "assigned"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      Assigned
                    </button>
                  </div>
                </div>
              </div>

              {/* Group Actions - Show when unassigned filter is selected AND orders are selected */}
              {shouldShowGroupActions && (
                <div className="delivery-panels__group-actions">
                  <button
                    onClick={() => setShowGroupModal(true)}
                    disabled={!canCreateGroup}
                    className={`delivery-panels__create-group-button ${
                      !canCreateGroup
                        ? "delivery-panels__create-group-button--disabled"
                        : ""
                    }`}
                  >
                    <Package size={18} />
                    Create Group
                  </button>

                  {regularGroups.length > 0 && (
                    <div className="delivery-panels__add-to-group">
                      <select
                        onChange={(e) =>
                          e.target.value && addToGroup(e.target.value)
                        }
                        className="delivery-panels__group-select"
                        defaultValue=""
                      >
                        <option value="">Add to Existing Group...</option>
                        {regularGroups.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Orders List */}
              <div className="delivery-panel">
                <h2 className="delivery-panel__title">
                  <Package className="delivery-panel__icon" />
                  Orders ({finalFilteredDeliveries.length})
                  {assignmentFilter === "unassigned" && " - Unassigned"}
                  {assignmentFilter === "assigned" && " - Assigned to Groups"}
                  {localSearchTerm && (
                    <span className="delivery-panel__search-indicator">
                      • Searching: "{localSearchTerm}"
                    </span>
                  )}
                </h2>
                <div className="delivery-panel__content">
                  {finalFilteredDeliveries.length === 0 ? (
                    <div className="delivery-panel__empty-state">
                      <Package size={32} />
                      <p>No orders found</p>
                      <p>Try changing your filters or search term</p>
                    </div>
                  ) : (
                    finalFilteredDeliveries.map((delivery) => {
                      const statusInfo = getOrderStatusInfo(
                        delivery.orderStatus,
                      );
                      const isInGroup =
                        delivery.groupId && delivery.status === "grouped";
                      const group =
                        isInGroup && delivery.groupId
                          ? groups[delivery.groupId]
                          : null;
                      const isSelectedForMap = selectedOrdersForMap.includes(
                        delivery.id,
                      );
                      const isSelectedForGroup = selectedDeliveries.includes(
                        delivery.id,
                      );

                      return (
                        <div
                          key={delivery.id}
                          className={`delivery-item ${
                            delivery.status === "unassigned" && !delivery.groupId
                              ? "delivery-item--selectable"
                              : ""
                          } ${
                            isSelectedForGroup ? "delivery-item--selected" : ""
                          } ${
                            isSelectedForMap
                              ? "delivery-item--map-selected"
                              : ""
                          }`}
                        >
                          {/* Checkbox for selection - different behavior based on eligibility */}
                          <div className="delivery-item__checkbox-container">
                            <input
                              type="checkbox"
                              checked={
                                delivery.status === "unassigned" && !delivery.groupId
                                  ? isSelectedForGroup
                                  : isSelectedForMap
                              }
                              onChange={() => {
                                if (delivery.status === "unassigned" && !delivery.groupId) {
                                  handleOrderSelectionForGroup(delivery.id);
                                } else {
                                  handleOrderSelectionForMap(delivery.id);
                                }
                              }}
                              className="delivery-item__checkbox-input"
                            />
                          </div>

                          <div
                            className="delivery-item__content"
                            onClick={() => {
                              if (delivery.status === "unassigned" && !delivery.groupId) {
                                handleOrderSelectionForGroup(delivery.id);
                              } else {
                                handleOrderSelectionForMap(delivery.id);
                              }
                            }}
                            onMouseEnter={() => setHoveredDelivery(delivery.id)}
                            onMouseLeave={() => setHoveredDelivery(null)}
                          >
                            <div className="delivery-item__header">
                              <div className="delivery-item__reference">
                                {delivery.orderReference}
                                {/* Add collection badge if grouped for collection */}
                                {delivery.groupedForCollection && (
                                  <span className="delivery-item__collection-badge">
                                    (Finalized Group)
                                  </span>
                                )}
                              </div>
                              <div
                                className="delivery-item__status"
                                style={{ backgroundColor: statusInfo.color }}
                              >
                                {statusInfo.text}
                              </div>
                            </div>
                            <div className="delivery-item__details">
                              <span className="delivery-item__detail">
                                <b>Customer:</b>{" "}
                                {delivery.customer?.displayName ||
                                  delivery.organization?.displayName ||
                                  "No Customer"}
                              </span>
                              <span className="delivery-item__detail">
                                <b>Phone:</b> {delivery.phoneNumber}
                              </span>
                              <span className="delivery-item__detail">
                                <b>Address:</b>{" "}
                                {delivery.deliveryDetails.address}
                              </span>
                              {isInGroup && group && (
                                <span className="delivery-item__detail">
                                  <b>Group:</b>
                                  <span
                                    className="delivery-item__group-tag"
                                    style={{
                                      color: group.color,
                                      borderColor: group.color,
                                    }}
                                  >
                                    {group.name}
                                  </span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}

          {workflowStep === "assign" && (
            <>
              {/* Group Type Filters */}
              <div className="delivery-panels__filters">
                <div className="delivery-panels__filter-group">
                  <div className="delivery-panels__filter-buttons">
                    <button
                      onClick={() => handleGroupTypeFilterChange("tentative")}
                      className={`delivery-panels__filter-button ${
                        groupTypeFilter === "tentative"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      Tentative
                    </button>
                    <button
                      onClick={() => handleGroupTypeFilterChange("finalized")}
                      className={`delivery-panels__filter-button ${
                        groupTypeFilter === "finalized"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      Finalized
                    </button>
                  </div>
                </div>

                {/* Tentative Sub-filters */}
                {groupTypeFilter === "tentative" && (
                  <div className="delivery-panels__filter-group">
                    <div className="delivery-panels__filter-buttons">
                      <button
                        onClick={() => handleTentativeFilterChange("all")}
                        className={`delivery-panels__filter-button ${
                          tentativeFilter === "all"
                            ? "delivery-panels__filter-button--active"
                            : ""
                        }`}
                      >
                        All
                      </button>
                      <button
                        onClick={() =>
                          handleTentativeFilterChange("unassigned")
                        }
                        className={`delivery-panels__filter-button ${
                          tentativeFilter === "unassigned"
                            ? "delivery-panels__filter-button--active"
                            : ""
                        }`}
                      >
                        Cart Unassign
                      </button>
                      <button
                        onClick={() => handleTentativeFilterChange("assigned")}
                        className={`delivery-panels__filter-button ${
                          tentativeFilter === "assigned"
                            ? "delivery-panels__filter-button--active"
                            : ""
                        }`}
                      >
                        Cart Assign
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Create Actual Group Orders Button - Show in tentative > cart assign */}
              {groupTypeFilter === "tentative" &&
                tentativeFilter === "assigned" && (
                  <div className="delivery-panels__create-actual-groups">
                    {/* Create Actual Group Orders Button */}
                    <div className="delivery-panels__create-button-wrapper">
                      <button
                        onClick={() => {
                          // Get selected assignable groups
                          const assignableGroups = getAssignableGroups();
                          const selectedAssignableGroups =
                            selectedGroupsForMap.filter((groupId) => {
                              const group = groups[groupId];
                              return (
                                group &&
                                !group.isCollectionGrouped &&
                                group.cart?.cartId
                              );
                            });

                          // If groups are selected, use them. Otherwise use all assignable groups
                          const groupsToCreate =
                            selectedAssignableGroups.length > 0
                              ? selectedAssignableGroups
                              : assignableGroups.map((g) => g.id);

                          if (groupsToCreate.length > 0) {
                            onCreateActualGroupOrders(groupsToCreate);
                          }
                        }}
                        disabled={
                          isCreatingActualGroups ||
                          getAssignableGroups().length === 0
                        }
                        className={`delivery-panels__create-actual-button ${
                          isCreatingActualGroups ||
                          getAssignableGroups().length === 0
                            ? "delivery-panels__create-actual-button--disabled"
                            : ""
                        }`}
                        title={
                          selectedGroupsForMap.length > 0
                            ? `Create actual groups for ${selectedGroupsForMap.length} selected group(s)`
                            : "Create actual groups for all assigned groups"
                        }
                      >
                        {isCreatingActualGroups ? (
                          <>
                            <Loader size={18} className="animate-spin" />
                            Creating Actual Groups...
                          </>
                        ) : (
                          <>
                            <Filter size={18} />
                            {selectedGroupsForMap.length > 0
                              ? `Create Actual Groups (${selectedGroupsForMap.length})`
                              : "Create Actual Group Orders"}
                          </>
                        )}
                      </button>
                    </div>

                    {getAssignableGroups().length === 0 && (
                      <p className="delivery-panels__create-actual-hint">
                        No assigned groups available to create actual orders
                      </p>
                    )}
                  </div>
                )}

              {/* Tentative Views */}
              {groupTypeFilter === "tentative" && (
                <>
                  {tentativeFilter === "all" && (
                    <GroupDetailsView
                      groups={groups}
                      deliveries={deliveries}
                      deleteGroup={deleteGroup}
                      onSaveGroupChanges={onSaveGroupChanges}
                      selectedGroupsForMap={selectedGroupsForMap}
                      toggleGroupSelectionForMap={toggleGroupSelectionForMap}
                      showOnlySelectedGroups={showOnlySelectedGroups}
                      setShowOnlySelectedGroups={setShowOnlySelectedGroups}
                      onOptimizePath={onOptimizePath}
                    />
                  )}

                  {tentativeFilter === "assigned" && (
                    <VehicleRoutes
                      key={`vehicle-routes-${
                        Object.values(groups).filter(
                          (g) => !g.isCollectionGrouped && g.cart,
                        ).length
                      }-${selectedGroupsForMap.length}`}
                      vehicles={vehicles}
                      deliveries={deliveries}
                      groups={groups}
                      setHoveredDelivery={setHoveredDelivery}
                      assignVehicleToGroup={assignVehicleToGroup}
                      removeCartFromGroup={removeCartFromGroup}
                      isAssigningCart={isAssigningCart}
                      assigningGroupId={assigningGroupId}
                      selectedCartsForMap={selectedCartsForMap}
                      toggleCartSelectionForMap={toggleCartSelectionForMap}
                      showOnlySelectedCarts={showOnlySelectedCarts}
                      setShowOnlySelectedCarts={setShowOnlySelectedCarts}
                      selectedGroupsForMap={selectedGroupsForMap}
                      toggleGroupSelectionForMap={toggleGroupSelectionForMap}
                      showOnlySelectedGroups={showOnlySelectedGroups}
                      setShowOnlySelectedGroups={setShowOnlySelectedGroups}
                      isCreatingActualGroups={isCreatingActualGroups}
                    />
                  )}

                  {tentativeFilter === "unassigned" && (
                    <div className="delivery-panel">
                      <h2 className="delivery-panel__title">
                        <Truck className="delivery-panel__icon" />
                        Assign Carts to Groups ({filteredGroups.length})
                      </h2>
                      <div className="delivery-panel__content">
                        <CartAssignmentSection
                          groups={filteredGroups}
                          vehicles={vehicles}
                          assignVehicleToGroup={assignVehicleToGroup}
                          getOptimizedRouteOrder={getOptimizedRouteOrder}
                          getVehicleDisplayName={getVehicleDisplayName}
                          isGroupAssigning={isGroupAssigning}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Finalized View - Show Actual Group Orders */}
              {groupTypeFilter === "finalized" && (
                <ActualGroupDetailsView
                  groups={collectionGroups}
                  deliveries={deliveries}
                  vehicles={vehicles}
                  selectedGroupsForMap={selectedGroupsForMap}
                  toggleGroupSelectionForMap={toggleGroupSelectionForMap}
                  showOnlySelectedGroups={showOnlySelectedGroups}
                  setShowOnlySelectedGroups={setShowOnlySelectedGroups}
                  onUpdateGroupOrderStatus={onUpdateGroupOrderStatus}
                  isUpdatingGroupStatus={isUpdatingGroupStatus}
                  updatingGroupId={updatingGroupId}
                  onRemoveOrderFromGroup={onRemoveOrderFromGroup}
                  isRemovingOrder={isRemovingOrder}
                  removingOrderId={removingOrderId}
                />
              )}
            </>
          )}

          {workflowStep === "actual" && (
            <>
              {/* Vehicle Filters */}
              <div className="delivery-panels__filters">
                <div className="delivery-panels__filter-group">
                  <div className="delivery-panels__filter-buttons">
                    <button
                      onClick={() => handleVehicleFilterChange("assigned")}
                      className={`delivery-panels__filter-button ${
                        vehicleFilter === "assigned"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      All Assigned ({getAssignedCarts().length})
                    </button>
                    <button
                      onClick={() => handleVehicleFilterChange("unassigned")}
                      className={`delivery-panels__filter-button ${
                        vehicleFilter === "unassigned"
                          ? "delivery-panels__filter-button--active"
                          : ""
                      }`}
                    >
                      Unassigned ({getUnassignedCarts().length})
                    </button>
                  </div>
                </div>
              </div>

              {/* Vehicle Routes for assigned carts */}
              {vehicleFilter === "assigned" && (
                <div className="delivery-panel">
                  <div className="delivery-panel__content">
                    <AssignedCartsSection
                      assignedCarts={getAssignedCarts()}
                      groups={groups}
                      expandedCarts={expandedCarts}
                      selectedCartsForMap={selectedCartsForMap}
                      isPrinting={isPrinting}
                      onToggleCartExpansion={toggleCartExpansion}
                      onToggleCartSelection={toggleCartSelectionForMap}
                      onShowOnlySelectedCarts={() => {
                        setShowOnlySelectedCarts(true);
                        setActiveMapFilter("group");
                      }}
                      onCartPrint={handleCartPrint}
                      getOptimizedRouteOrder={getOptimizedRouteOrder}
                    />
                  </div>
                </div>
              )}

              {/* Unassigned carts view */}
              {vehicleFilter === "unassigned" && (
                <div className="delivery-panel">
                  <h2 className="delivery-panel__title">
                    <Truck className="delivery-panel__icon" />
                    Unassigned Carts ({getUnassignedCarts().length})
                  </h2>
                  <div className="delivery-panel__content">
                    {renderUnassignedCartsSection()}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Show message when no movable carts available (only in assign step) */}
          {workflowStep === "assign" &&
            groupTypeFilter === "tentative" &&
            tentativeFilter === "unassigned" &&
            movableCarts.length === 0 &&
            filteredGroups.length > 0 && (
              <div className="delivery-panel">
                <div className="delivery-panel__empty-state">
                  <Truck size={48} />
                  <h3>No Movable Carts Available</h3>
                  <p>
                    There are no movable carts (isFixedLocation: false)
                    available for assignment.
                  </p>
                  <p>All available carts are fixed location carts.</p>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryPanels;
